import React from 'react';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, Video } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { BACKEND_URL } from '../../../lib/site';
import { genUid } from '../leadUtils';

const resolveSrc = (url) => (url && url.startsWith('/api/') ? `${BACKEND_URL}${url}` : url);

export const VideosCard = ({ content, setContent, save, saving, authAxios }) => {
  const setVideo = (i, k, value) => {
    setContent((c) => {
      const videos = [...(c.videos || [])];
      videos[i] = { ...videos[i], [k]: value };
      return { ...c, videos };
    });
  };

  const uploadSectionVideo = async (file) => {
    if (!file) return;
    const toastId = toast.loading('Uploading video\u2026');
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await authAxios.post('/admin/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000,
      });
      setContent((c) => ({
        ...c,
        videos: [...(c.videos || []), { _uid: genUid('ct'), title: '', section: 'applications', file_url: res.data.url }],
      }));
      toast.success('Video uploaded \u2014 set a title/section and Save Videos', { id: toastId });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Video upload failed', { id: toastId });
    }
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="font-display font-semibold text-lg flex items-center gap-2">
            <Video className="w-4.5 h-4.5 text-[#155E6D]" size={18} /> Site Videos
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Showcase construction videos inside the Applications or Process sections (MP4/WebM/MOV, max 60 MB). They play
            slowly, muted and looped. Nothing is shown on the website until you save.
          </p>
        </div>
        <div>
          <input
            type="file"
            accept="video/mp4,video/webm,video/quicktime"
            className="hidden"
            id="site-video-upload"
            data-testid="admin-content-video-file-input"
            onChange={(e) => {
              uploadSectionVideo(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
          <Button
            variant="outline"
            size="sm"
            data-testid="admin-content-upload-video-button"
            onClick={() => document.getElementById('site-video-upload').click()}
          >
            <Plus className="w-4 h-4 mr-1" /> Upload Video
          </Button>
        </div>
      </div>

      <div className="mt-5 space-y-4">
        {(content.videos || []).length === 0 && (
          <p className="text-sm text-muted-foreground" data-testid="admin-content-videos-empty">
            No site videos yet. Upload one to feature it on the website.
          </p>
        )}
        {(content.videos || []).map((v, i) => (
          <div key={v._uid} className="border border-border rounded-lg p-4 grid sm:grid-cols-[160px_1fr] gap-4 items-start" data-testid={`admin-content-video-${i}`}>
            <video
              src={resolveSrc(v.file_url)}
              className="w-full aspect-video object-cover rounded-md border border-border bg-muted"
              muted
              playsInline
              preload="metadata"
            />
            <div className="grid sm:grid-cols-2 gap-3">
              <Input
                placeholder="Title / caption (e.g. Wall panel installation)"
                value={v.title || ''}
                onChange={(e) => setVideo(i, 'title', e.target.value)}
                className="h-10"
                data-testid={`admin-content-video-title-${i}`}
              />
              <div className="flex gap-2">
                <Select value={v.section || 'applications'} onValueChange={(val) => setVideo(i, 'section', val)}>
                  <SelectTrigger className="h-10 flex-1" data-testid={`admin-content-video-section-${i}`}>
                    <SelectValue placeholder="Section" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="applications">Applications section</SelectItem>
                    <SelectItem value="process">Process (How We Build) section</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-10 w-10 shrink-0 text-destructive"
                  aria-label="Delete video"
                  data-testid={`admin-content-video-delete-${i}`}
                  onClick={() => setContent((c) => ({ ...c, videos: (c.videos || []).filter((_, j) => j !== i) }))}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      <Button className="h-10 mt-5" disabled={saving === 'videos'} onClick={() => save('videos')} data-testid="admin-content-videos-save-button">
        {saving === 'videos' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
        Save Videos
      </Button>
    </div>
  );
};
